import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "../../constants/routes.js";
import { useDeleteTasksMutation } from "../../store/api/tasksApi.js";
import { recentTaskRemoved } from "../../store/slices/recentTasksSlice.js";
import SearchIcon from "../Icons/searchIcon.jsx";
import {
  dateLabel,
  groupByBucket,
  ownsTaskId,
  runDotsOf,
  runRowsOf,
  RUN_SLOTS,
  SOURCE,
  sourceNounOf,
  sourceOf,
  statusKind,
  timeLabel,
  titleOf,
} from "./taskMeta.js";

export default function SidebarRecents({
  recentTasks = [],
  loading = false,
  onTaskDeleted,
}) {
  // C1: `recentTasks` is sourced from `useGetRecentTasksQuery` in the
  // parent Sidebar (via RTK Query). R8: the delete itself is now a
  // `useDeleteTasksMutation` hook on tasksApi — `invalidatesTags:
  // ['Tasks']` makes the sidebar + Home actions-required refetch
  // automatically, so the manual `dispatch(invalidateTags)` from C1
  // is gone. The `onTaskDeleted` prop is still called for any parent
  // that wants to react to a delete event.
  const [deleteTasks] = useDeleteTasksMutation();
  const dispatch = useDispatch();
  const [isRecentsOpen, setIsRecentsOpen] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  // Client-side filter query. We filter the already-loaded list rather
  // than hitting the backend — the sidebar holds at most ~50 rows and
  // every field we'd search on (title, subtitle source) is already
  // denormalised onto each row. No debounce needed at this size.
  const [searchQuery, setSearchQuery] = useState("");
  // Search bar is hidden by default; the search icon in the header
  // toggles it. Kept separate from `searchQuery` so closing the bar
  // only hides the chrome — if the user re-opens it, their query is
  // still there. Clearing is explicit via the × button.
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Hover tooltip for recent rows — portalled to <body> and positioned
  // off the hovered row's rect so it escapes the scrollable list's
  // overflow clipping. Shows the full (untruncated) title plus the
  // task's date · time.
  const [hoverTip, setHoverTip] = useState(null);
  const showTip = (e, meta) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverTip({
      ...meta,
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  };
  const hideTip = () => setHoverTip(null);

  const requestDelete = (e, task) => {
    e.stopPropagation();
    // The whole run tree that gets deleted — original run + every draft.
    // Shown as a list in the confirm modal so the user sees exactly what
    // (and how many) they're removing.
    const runs = runRowsOf(task);
    setPendingDelete({
      id: String(task?.task_id ?? task?.id ?? ""),
      title: titleOf(task) || "",
      runs,
      // Modal heading names the kind of task ("Delete New Task" /
      // "Delete Template Task" / "Delete Trigger Task" / "Delete Relic Task").
      typeNoun: sourceNounOf(task),
      // "New task" AND template rows branch draft versions, so both get
      // the full run-tree card — mirroring `runDotsOf`, which lights the
      // sidebar run-dot meter for exactly these two sources. Trigger /
      // relic runs don't branch drafts, so they keep the SIMPLE popup
      // (header + copy + buttons) — same reason their RUNS version box
      // and run-dots stay hidden.
      showRuns: [SOURCE.TASK, SOURCE.TEMPLATE].includes(sourceOf(task)),
      // Any run — the parent OR any subtask/draft — still mid-flight
      // (running or interrupt/paused). When true the confirm copy warns
      // the run gets cancelled first and the CTA becomes "Cancel & delete
      // task"; otherwise it's a plain "Delete task".
      hasActiveRun: runs.some(
        (r) => r.kind === "running" || r.kind === "interrupt",
      ),
    });
  };
  const closeDeleteModal = () => setPendingDelete(null);

  const modalRoot = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.body;
  }, []);

  // Close confirm on Escape
  useEffect(() => {
    if (!pendingDelete) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeDeleteModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pendingDelete]);

  // Auto-focus the search input when the bar opens. Waiting one frame
  // past the open-state flip lets the slide-in CSS transition start
  // from height 0 — focusing before the paint would jump the caret
  // into a collapsed input. Closing the bar also clears any in-flight
  // query so a stale filter doesn't stick around after the user hides
  // the chrome.
  useEffect(() => {
    if (!isSearchOpen) {
      setSearchQuery("");
      return;
    }
    const id = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isSearchOpen]);

  const confirmDelete = async () => {
    try {
      const taskId = pendingDelete?.id;
      if (!taskId) return;
      // `invalidatesTags: ['Tasks']` refetches the Home Tasks/*
      // providers (ACTIONS_REQUIRED + EVENTS_*). `.unwrap()` re-throws on
      // rejection so the catch below still fires for 4xx/5xx.
      await deleteTasks(taskId).unwrap();
      // The Sidebar list is stream-fed, not tag-invalidated — drop the
      // row now for instant feedback; the next snapshot reconciles.
      dispatch(recentTaskRemoved(taskId));
      onTaskDeleted?.(taskId);
      closeDeleteModal();
      // Only redirect to /task if the deleted task is the one currently
      // open — otherwise the user is mid-flow on a different task and a
      // forced navigation throws away their context.
      const prefix = `${ROUTES.TASK}/`;
      const openId = pathname.startsWith(prefix)
        ? pathname.slice(prefix.length).replace(/\/$/, "")
        : null;
      if (openId === taskId) navigate(ROUTES.TASK);
    } catch (err) {
      console.error("Failed to delete task:", err);
    }
  };

  // Extract active task id from current URL
  const recentPrefix = `${ROUTES.TASK}/`;
  const activeId = pathname.startsWith(recentPrefix)
    ? pathname.slice(recentPrefix.length).replace(/\/$/, "")
    : null;

  // Filter by title + status label + source label (case-insensitive)
  // before bucketing, so the date headers only render for buckets that
  // actually have matches under the current query.
  const filteredTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return recentTasks;
    return recentTasks.filter((task) => {
      const title = (titleOf(task) || "").toLowerCase();
      const statusLabel = (
        STATUS_META[statusKind(task)]?.label || ""
      ).toLowerCase();
      const sourceLabel = (SOURCE_LABEL[sourceOf(task)] || "").toLowerCase();
      return (
        title.includes(q) || statusLabel.includes(q) || sourceLabel.includes(q)
      );
    });
  }, [recentTasks, searchQuery]);

  // Group by date bucket. If no task carries created_at (backend not
  // yet expanded), everything falls into the single "Older" bucket
  // which renders as one flat list — same visual as today but already
  // reading the field once it arrives.
  const groups = useMemo(() => groupByBucket(filteredTasks), [filteredTasks]);

  const isSearching = searchQuery.trim().length > 0;
  const hasMatches = filteredTasks.length > 0;

  // Delete-confirm copy — a bold lead warning plus a detail line. The
  // lead changes when a run is still mid-flight (that run gets cancelled
  // before the delete lands); the detail names "drafts" only for sources
  // that branch them (new task / template), matching `showRuns`.
  // Lower-cased kind noun ("task" / "template task" / "trigger task" /
  // "relic task") so the lead names what's being removed in running
  // prose, mirroring the Title-Cased heading above it.
  const deleteNoun = (pendingDelete?.typeNoun || "Task").toLowerCase();
  const deleteCopy = pendingDelete
    ? {
        lead: pendingDelete.hasActiveRun
          ? `Warning: This ${deleteNoun} still has a run in progress.`
          : `Warning: You\u2019re about to permanently delete this ${deleteNoun}.`,
        detail: pendingDelete.hasActiveRun
          ? pendingDelete.showRuns
            ? "The active run will be cancelled, then this task and all associated runs, drafts, and their outputs are deleted. This action cannot be undone."
            : "The active run will be cancelled, then this task and all its outputs are deleted. This action cannot be undone."
          : pendingDelete.showRuns
            ? "All associated runs, drafts, and their outputs will also be deleted. This action cannot be undone."
            : "All its runs and outputs will also be deleted. This action cannot be undone.",
      }
    : null;

  return (
    <div className="sidebar-recents">
      {/* Header is a flex row of two separately-clickable targets: the
          title/chevron (collapses the section) and the search icon
          (toggles the inline search bar). Using a div wrapper rather
          than a single <button> keeps each affordance independently
          keyboard-focusable and avoids nested-interactive a11y. */}
      <div className="sidebar-recents-head">
        <button
          type="button"
          className="sidebar-recents-head-toggle"
          onClick={() => setIsRecentsOpen((prev) => !prev)}
          aria-expanded={isRecentsOpen}
        >
          <span className="sidebar-recents-title">My Tasks</span>
        </button>
        {!loading && recentTasks.length > 0 && (
          <button
            type="button"
            className={`sidebar-recents-search-toggle${isSearchOpen ? " is-open" : ""}`}
            onClick={() => {
              // Opening the search also expands the section — a hidden
              // search bar would defeat the whole affordance.
              if (!isRecentsOpen) setIsRecentsOpen(true);
              setIsSearchOpen((prev) => !prev);
            }}
            aria-label={isSearchOpen ? "Close search" : "Search my tasks"}
            aria-pressed={isSearchOpen}
            title={isSearchOpen ? "Close search" : "Search"}
          >
            <SearchIcon size={13} />
          </button>
        )}
        <button
          type="button"
          className="sidebar-recents-collapse-toggle"
          onClick={() => setIsRecentsOpen((prev) => !prev)}
          aria-label={isRecentsOpen ? "Collapse my tasks" : "Expand my tasks"}
          aria-expanded={isRecentsOpen}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: isRecentsOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s",
            }}
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {/* Animated slide-down container. `is-open` drives the CSS
          transition (max-height 0 → 40px + opacity 0 → 1) so the bar
          eases in/out instead of snapping. Rendered unconditionally
          when the section is expanded AND there's data to filter —
          the collapsed-to-0 state is invisible but keeps the
          transition anchors in the DOM. */}
      {isRecentsOpen && !loading && recentTasks.length > 0 && (
        <div
          className={`sidebar-recents-search-wrap${isSearchOpen ? " is-open" : ""}`}
          aria-hidden={!isSearchOpen}
        >
          <div className="sidebar-recents-search">
            <SearchIcon className="sb-search-icon" size={12} />
            <input
              ref={searchInputRef}
              type="text"
              className="sb-search-input"
              placeholder="Search tasks"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setIsSearchOpen(false);
              }}
              tabIndex={isSearchOpen ? 0 : -1}
              aria-label="Search my tasks"
            />
            {isSearching && (
              <button
                type="button"
                className="sb-search-clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {isRecentsOpen && (
        <div className="sidebar-recents-list">
          {loading ? (
            [1, 2, 3, 4].map((n) => (
              <div key={n} className="sidebar-recent-item sidebar-recent-skel">
                <span className="sidebar-skel-line" />
              </div>
            ))
          ) : isSearching && !hasMatches ? (
            <div className="sb-empty">
              <span>No tasks match &ldquo;{searchQuery.trim()}&rdquo;</span>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.label} className="sb-bucket">
                {/* Suppress the "Older" header when it's the only bucket
                    (i.e. no task had a created_at). Shows a flat list
                    exactly like today until the backend expands. */}
                {!(groups.length === 1 && group.label === "Older") && (
                  <div className="sb-bucket-head">{group.label}</div>
                )}
                {group.tasks.map((task, index) => {
                  const id = String(
                    task?.task_id ?? task?.id ?? `${group.label}-${index}`,
                  );
                  const title = titleOf(task);
                  // A row stands for the whole run tree, so it stays lit
                  // while the user is on any of its drafts — picking a
                  // run in the pinned panel navigates to the SUBTASK id,
                  // which has no row of its own.
                  const isActive = ownsTaskId(task, activeId);
                  const source = sourceOf(task);
                  const sk = statusKind(task);
                  const runDots = runDotsOf(task);
                  const time = timeLabel(task);
                  const date = dateLabel(task);
                  const statusMeta = STATUS_META[sk];
                  const sourceLabel = SOURCE_LABEL[source];
                  return (
                    <div
                      key={id}
                      className={
                        `sidebar-recent-item sb-row` +
                        (isActive ? " sb-row--active" : "") +
                        (sk === "cancelled" ? " sb-row--cancelled" : "")
                      }
                      onMouseEnter={(e) =>
                        showTip(e, {
                          title: title || "Untitled task",
                          time,
                          date,
                          runs: runRowsOf(task),
                          // "New task" AND template rows show their run
                          // history in the tooltip even with just the single
                          // Original run (templates now branch drafts via
                          // Edit & re-run, so they match a new task); trigger
                          // / relic rows keep the "only when there are
                          // drafts" rule.
                          alwaysShowRuns:
                            source === SOURCE.TASK ||
                            source === SOURCE.TEMPLATE,
                        })
                      }
                      onMouseLeave={hideTip}
                      onClick={() => {
                        hideTip();
                        navigate(`${ROUTES.TASK}/${id}`);
                      }}
                    >
                      <div className="sb-row-body">
                        <div className="sb-row-title">
                          {/* Status dot hidden per request. Uncomment to
                              restore the coloured leading dot on each row. */}
                          {/* {sk !== "cancelled" && (
                            <span
                              className={`sb-dot sb-dot--${sk}`}
                              aria-hidden="true"
                            />
                          )} */}
                          <span className="sb-row-title-text">
                            {title || "Untitled task"}
                          </span>
                        </div>
                        <div className="sb-row-sub">
                          {/* Run meter — one dot per run (original +
                              drafts) plus the unused slots, shown only on
                              tasks that HAVE drafts. It stands in for the
                              status text on those rows: the dots already
                              carry each run's state, and the hover
                              tooltip spells them out. */}
                          {runDots.length > 0 ? (
                            <span
                              className="sb-run-dots"
                              aria-label={`${runDots.filter((k) => k !== "empty").length} of ${runDots.length} runs used`}
                              role="img"
                            >
                              {runDots.map((kind, di) => (
                                <span
                                  key={di}
                                  className={`sb-run-dot sb-run-dot--${kind}`}
                                />
                              ))}
                            </span>
                          ) : (
                            statusMeta && (
                              <>
                                <span
                                  className={`sb-status ${statusMeta.className}`}
                                >
                                  {statusMeta.label}
                                </span>
                                {(time || sourceLabel) && (
                                  <span
                                    className="sb-row-sub-sep"
                                    aria-hidden="true"
                                  >
                                    |
                                  </span>
                                )}
                              </>
                            )
                          )}
                          {time && (
                            <>
                              <span>{time}</span>
                              {sourceLabel && (
                                <span
                                  className="sb-row-sub-sep"
                                  aria-hidden="true"
                                >
                                  {runDots.length > 0 ? "·" : "|"}
                                </span>
                              )}
                            </>
                          )}
                          {sourceLabel && <span>{sourceLabel}</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="sidebar-recent-delete"
                        aria-label={`Delete ${title}`}
                        onClick={(e) => requestDelete(e, task)}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {hoverTip && modalRoot
        ? createPortal(
            <div
              className="sb-tip"
              role="tooltip"
              style={{ top: hoverTip.top, left: hoverTip.left }}
            >
              <div className="sb-tip-title">
                {truncateToWords(hoverTip.title, 30)}
              </div>
              {hoverTip.date || hoverTip.time ? (
                <div className="sb-tip-meta">
                  {[hoverTip.date, hoverTip.time].filter(Boolean).join(" · ")}
                </div>
              ) : null}
              {/* Run history — Parent = "Original run", each subtask =
                  "Draft N", coloured by status. Shown when the task has
                  drafts (more than just the Original run) OR, for a "New
                  task" / template, always — so a single-run row still shows
                  its Original run. */}
              {Array.isArray(hoverTip.runs) &&
              (hoverTip.runs.length > 1 ||
                (hoverTip.alwaysShowRuns && hoverTip.runs.length > 0)) ? (
                <div className="sb-tip-runs">
                  {hoverTip.runs.map((run, i) => (
                    <div className="sb-tip-run" key={i}>
                      <span
                        className={`sb-tip-run-dot sb-tip-run-dot--${run.kind}`}
                        aria-hidden="true"
                      />
                      <span className="sb-tip-run-label">{run.label}</span>
                      {run.statusLabel ? (
                        <span
                          className={`sb-tip-run-status sb-tip-run-status--${run.kind}`}
                        >
                          {run.statusLabel}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>,
            modalRoot,
          )
        : null}

      {pendingDelete
        ? modalRoot
          ? createPortal(
              <div
                className="sdm-overlay"
                onClick={closeDeleteModal}
                role="dialog"
                aria-modal="true"
                aria-label="Confirm delete recent task"
              >
                <div className="sdm-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="sdm-head">
                    <span className="sdm-head-icon" aria-hidden="true">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </span>
                    <div className="sdm-title">
                      Delete {pendingDelete.typeNoun}?
                    </div>
                  </div>
                  <div className="sdm-body">
                    <strong className="sdm-body-lead">{deleteCopy.lead}</strong>{" "}
                    {deleteCopy.detail}
                  </div>
                  {/* Full run tree — original run + drafts, with a "used /
                      total slots" count. Shown for "New task" AND template
                      rows; the dots reuse the sidebar's token-driven status
                      colours. Trigger / relic keep a simple popup. */}
                  {pendingDelete.showRuns && pendingDelete.runs?.length > 0 && (
                    <div className="sdm-runs">
                      <div className="sdm-runs-head">
                        <span className="sdm-runs-head-label">
                          {pendingDelete.title || "this task"}
                        </span>
                        <span className="sdm-runs-count">
                          {pendingDelete.runs.length} / {RUN_SLOTS} runs
                        </span>
                      </div>
                      <ul className="sdm-runs-list">
                        {pendingDelete.runs.map((run, i) => {
                          // Any run — original OR a draft/subtask — whose
                          // backend status is exactly "finished"
                          // (case-insensitive) was kept as a reusable
                          // template → "Template saved" chip. Every other
                          // run carries no chip; its coloured dot conveys
                          // the status.
                          const finished =
                            String(run.status ?? "")
                              .trim()
                              .toLowerCase() === "finished";
                          return (
                            <li className="sdm-run" key={i}>
                              <span
                                className={`sb-run-dot sb-run-dot--${run.kind}`}
                                aria-hidden="true"
                              />
                              <span className="sdm-run-text">
                                <span className="sdm-run-label">
                                  {run.label}
                                </span>
                                <span className="sdm-run-name">
                                  {run.name || run.label}
                                </span>
                              </span>
                              {finished ? (
                                <span className="sdm-run-chip sdm-run-chip--template">
                                  Template saved
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  <div className="sdm-actions">
                    <button
                      type="button"
                      className="sdm-btn"
                      onClick={closeDeleteModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="sdm-btn sdm-btn--danger"
                      onClick={confirmDelete}
                    >
                      {pendingDelete.hasActiveRun
                        ? "Cancel & delete task"
                        : "Delete task"}
                    </button>
                  </div>
                </div>
              </div>,
              modalRoot,
            )
          : null
        : null}
    </div>
  );
}

// ── Subtitle metadata maps (HIQ-11 redesign) ──────────────────────────
// The row subtitle now reads `Status | Time | Source`, with the status
// chunk colored to match the dot. Only the statusKind keys the design
// covers are mapped here — `done` / `idle` deliberately produce no
// status word (the subtitle falls back to `Time | Source` for those
// rows). Keep this purely visual: no new logic, statusKind is the
// source of truth.

const STATUS_META = {
  running: { label: "Active", className: "sb-status--running" },
  interrupt: { label: "Action Required", className: "sb-status--interrupt" },
  failed: { label: "Fail", className: "sb-status--failed" },
  cancelled: { label: "Cancelled", className: "sb-status--cancelled" },
  done: { label: "Completed", className: "sb-status--done" },
};

const SOURCE_LABEL = {
  task: "New task",
  template: "Template Task",
  trigger: "Trigger",
  relic: "Relic",
};

// Cap the My Tasks hover tooltip's title at N words. The title is
// the user's typed prompt (or a synthesised "New task" placeholder),
// which can be arbitrarily long when the user pasted a multi-
// paragraph instruction. A 30-word ceiling keeps the tooltip
// compact (per UX) while still surfacing enough context to
// distinguish similar tasks at a glance. Append "…" only when
// truncation actually happens.
function truncateToWords(text, maxWords) {
  if (typeof text !== "string") return "";
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(" ") + "…";
}
